import { useState } from "react";
import { LayoutTemplate, Plus, Upload, Settings2, ArrowRightLeft, ExternalLink, Terminal, Server, Archive, Settings } from "lucide-react";
import { api } from "@/data/api";
import { useAsync } from "@/hooks/useAsync";
import { PageHeader, StatusBadge, Panel, DemoBadge, AsyncBoundary, CardSkeleton } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { demoAction } from "@/lib/demo";
import { cn } from "@/lib/utils";

const IMPORT_ICONS = { wordpress: LayoutTemplate, ssh: Terminal, cpanel: Server, backup: Archive, manual: Settings };

const ImportWizard = () => {
  const { data } = useAsync(() => api.getImportMethods());
  const [selected, setSelected] = useState(null);
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-line bg-transparent hover:bg-white/5" data-testid="import-site"><Upload className="h-4 w-4" /> Importar Site</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl border-line bg-popover">
        <DialogHeader>
          <DialogTitle className="font-heading">Importar Website</DialogTitle>
          <DialogDescription>Escolha o método de importação. Apenas UI nesta etapa — a migração real será conectada depois.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2 sm:grid-cols-2">
          {(data || []).map((m) => {
            const Icon = IMPORT_ICONS[m.icon] || Upload;
            return (
              <button key={m.key} onClick={() => setSelected(m.key)} data-testid={`import-method-${m.key}`}
                className={cn("flex items-start gap-3 rounded-lg border p-3 text-left transition-colors", selected === m.key ? "border-brand/50 bg-brand/5" : "border-line bg-white/[0.02] hover:bg-white/[0.04]")}>
                <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg", selected === m.key ? "bg-brand/15 text-brand" : "bg-white/5 text-muted-foreground")}><Icon className="h-5 w-5" /></div>
                <div><p className="text-sm font-semibold text-foreground">{m.title}</p><p className="text-xs text-muted-foreground">{m.desc}</p></div>
              </button>
            );
          })}
        </div>
        <Button disabled={!selected} onClick={() => demoAction(`Importar via ${selected}`)} className="bg-brand text-white hover:bg-brand-hover" data-testid="import-continue">Continuar</Button>
      </DialogContent>
    </Dialog>
  );
};

export default function WordPress() {
  const { data, loading, error } = useAsync(() => api.getWordpressSites());
  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader icon={LayoutTemplate} title="WordPress" description="Sites WordPress gerenciados. Provisionamento e migração reais serão conectados depois."
        breadcrumb={<><span>Aplicações</span><span>/</span><span className="text-foreground">WordPress</span></>}
        actions={<><DemoBadge className="hidden sm:inline-flex" /><ImportWizard /><Button onClick={() => demoAction("Criar WordPress")} className="bg-brand text-white hover:bg-brand-hover" data-testid="wp-create"><Plus className="h-4 w-4" /> Criar WordPress</Button></>} />

      <AsyncBoundary loading={loading} error={error} skeleton={<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)}</div>}>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data?.map((wp) => (
            <div key={wp.id} className="rounded-xl border border-line bg-surface p-4" data-testid={`wp-site-${wp.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-info/10 text-info"><LayoutTemplate className="h-5 w-5" /></div>
                  <div><p className="font-heading font-semibold text-foreground">{wp.name}</p><p className="font-mono text-xs text-muted-foreground">{wp.domain}</p></div>
                </div>
                <StatusBadge status={wp.status} size="sm" />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                {[["WP", wp.version], ["PHP", wp.php], ["Servidor", wp.server], ["Plugins", wp.plugins]].map(([k, v]) => (
                  <div key={k} className="flex justify-between rounded border border-line/60 bg-white/[0.02] px-2 py-1.5"><span className="text-muted-foreground">{k}</span><span className="font-medium text-foreground">{v}</span></div>
                ))}
              </div>
              {wp.updates > 0 && <p className="mt-3 text-xs text-warn">{wp.updates} atualização(ões) disponível(is)</p>}
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => demoAction(`Gerenciar ${wp.name}`)} className="flex-1 border-line bg-transparent hover:bg-white/5" data-testid={`wp-manage-${wp.id}`}><Settings2 className="h-4 w-4" /> Gerenciar</Button>
                <Button size="sm" variant="outline" onClick={() => demoAction(`Migrar ${wp.name}`)} className="border-line bg-transparent hover:bg-white/5" data-testid={`wp-migrate-${wp.id}`}><ArrowRightLeft className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => demoAction(`Abrir ${wp.domain}`)} className="border-line bg-transparent hover:bg-white/5"><ExternalLink className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </AsyncBoundary>
    </div>
  );
}
